import {
    initSocket,
    disconnectSocket,
    getSocket,
} from '@/services/socket/websocket.service.js'
import { useAuthStore } from '@/stores/auth.store'
import { useConversationStore } from '@/stores/conversation.store'
import { useCallStore } from '@/stores/call.store'
import userNotificationSocketService from '@/services/socket/userNotificationSocket.service.js'
import adminSocketService from '@/services/socket/admin.socket.service.js'
import callSocketService from '@/services/socket/callSocket.service.js'
import { tokenManager } from '@/services/apiClient'
import { initializeConversationSocketHandlers } from '@/services/socket/conversationSocketManager.js'
import dialService from '@/services/api/dial.service.js'

let isInitialized = false
let initializationInProgress = false

/**
 * Initializes all application socket connections and listeners.
 * This should be called after a user is authenticated and has an ID token.
 * @param {string} idToken - The Firebase ID token for the authenticated user.
 */
export async function initializeAppSockets(idToken) {
    if (isInitialized) {
        console.log('App sockets already initialized.')
        return
    }

    if (initializationInProgress) {
        console.log('Socket initialization already in progress, waiting...')
        return
    }

    initializationInProgress = true
    console.log('Initializing application-wide sockets...')

    try {
        // 1. Initialize the main WebSocket connection with the provided token
        // Only refresh if not provided or invalid
        const tokenToUse = idToken || (await tokenManager.getToken(false))
        if (!tokenToUse) {
            throw new Error('Failed to get authentication token')
        }

        await initSocket(tokenToUse)
        const socket = getSocket()
        if (!socket) {
            throw new Error('Failed to initialize the main socket connection')
        }
        console.log('Main socket connection established.')

        // Wait for socket to be fully connected
        if (!socket.connected) {
            console.log('Waiting for socket connection to be established...')
            await new Promise((resolve) => {
                const timeout = setTimeout(() => {
                    console.warn('Socket connection timeout reached')
                    resolve() // Resolve anyway to prevent hanging
                }, 5000)

                socket.once('connect', () => {
                    clearTimeout(timeout)
                    console.log('Socket connected:', socket.id)
                    resolve()
                })
            })
        }

        // Initialize stores
        const authStore = useAuthStore()
        const conversationStore = useConversationStore()
        const callStore = useCallStore()

        // 2. FIRST SET UP ALL EVENT LISTENERS before joining rooms
        console.log('Setting up socket event listeners...')

        // Set up conversation socket listeners directly using the manager
        initializeConversationSocketHandlers(conversationStore, authStore)
        console.log('Conversation socket listeners initialized.')

        // Set up call socket listeners
        callSocketService.setupCallSocketListeners(callStore, authStore)
        console.log('Call socket listeners initialized.')

        // Set up user notification socket listeners
        userNotificationSocketService.setupUserNotificationSocketListeners()
        console.log('User notification listeners initialized.')

        console.log('All socket event listeners registered successfully')

        // Small delay to ensure listeners are fully registered
        await new Promise((resolve) => setTimeout(resolve, 100))

        // 3. NOW JOIN CHANNELS after all listeners are registered

        // Join user notification channels
        try {
            await userNotificationSocketService.joinUserNotificationChannel()
            console.log('User notification channels joined.')
        } catch (error) {
            console.error('Error joining user notification channels:', error)
            throw error
        }

        // 4. Initialize Conversation Store & join conversation rooms
        if (authStore.user.data) {
            try {
                await conversationStore.initializeStore()
                console.log('Conversation store and its sockets initialized.')
            } catch (error) {
                console.error(
                    'Error initializing conversation store sockets:',
                    error
                )
                throw error
            }
        } else {
            console.warn(
                'Cannot initialize conversation store sockets: Auth user data not ready.'
            )
        }

        // 5. Initialize Call Store & its Sockets
        try {
            await callStore.initializeCallSockets()
            console.log('Call store and its sockets initialized.')
        } catch (error) {
            console.error('Error initializing call store sockets:', error)
            throw error
        }

        // 6. Check for pending incoming calls (fallback for push notification scenario)
        try {
            const pendingCallsResponse = await dialService.getPendingCalls()
            if (pendingCallsResponse?.data?.pendingCalls?.length > 0) {
                const pendingCall = pendingCallsResponse.data.pendingCalls[0]
                console.log('Found pending incoming call:', pendingCall.orderId)
                // Trigger the incoming dial UI
                callStore.handleIncomingDial({
                    orderId: pendingCall.orderId,
                    caller: { userId: pendingCall.clientId },
                    clientName: pendingCall.clientName,
                    clientPhoto: pendingCall.clientPhoto,
                    callType: pendingCall.callType,
                    expiresAt: pendingCall.expiresAt,
                    perMinuteRate: pendingCall.perMinuteRate,
                })
            }
        } catch (error) {
            console.error('Error checking for pending calls:', error)
            // Non-critical, don't throw
        }

        // 7. Setup Admin Sockets if applicable
        if (authStore.user.data?.isAdmin) {
            try {
                await adminSocketService.joinAdminCallsChannel()
                console.log('Admin-specific socket listeners setup.')
            } catch (error) {
                console.error('Error setting up admin sockets:', error)
                // Don't throw for admin sockets, as they're not critical
            }
        }

        isInitialized = true
        console.log('Application-wide sockets initialization complete.')
    } catch (error) {
        console.error('Error during socket initialization:', error)
        // Clean up any partially initialized sockets
        await cleanupAppSockets()
        throw error // Re-throw to let caller handle the error
    } finally {
        initializationInProgress = false
    }
}

/**
 * Cleans up all application socket connections and listeners.
 * This should be called on user logout or initialization failure.
 */
export async function cleanupAppSockets() {
    if (!isInitialized && !initializationInProgress) {
        console.log('App sockets not initialized or already cleaned up.')
        return
    }
    console.log('Cleaning up application-wide sockets...')

    const conversationStore = useConversationStore()
    const callStore = useCallStore()

    // 1. First cleanup User Notification Sockets to ensure its tracking Set is cleared
    try {
        userNotificationSocketService.cleanupUserNotificationSocketListeners()
        console.log('User notification socket listeners cleaned up.')
    } catch (error) {
        console.error('Error cleaning up user notification sockets:', error)
    }

    // 2. Then cleanup Conversation Store Sockets
    try {
        await conversationStore.logoutClean()
        console.log('Conversation store sockets cleaned up.')
    } catch (error) {
        console.error('Error cleaning up conversation store sockets:', error)
    }

    // 3. Cleanup Call Store Sockets
    try {
        await callStore.logoutClean()
        console.log('Call store sockets cleaned up.')
    } catch (error) {
        console.error('Error cleaning up call store sockets:', error)
    }

    // 4. Cleanup Admin Sockets
    try {
        adminSocketService.cleanupEventListeners()
        console.log('Admin socket listeners cleaned up.')
    } catch (error) {
        console.error('Error cleaning up admin sockets:', error)
    }

    // 5. Disconnect the main socket
    if (getSocket()) {
        console.log('Ensuring main socket is disconnected.')
        disconnectSocket()
    }

    isInitialized = false
    initializationInProgress = false
    console.log('Application-wide sockets cleanup complete.')
}

export default {
    initializeAppSockets,
    cleanupAppSockets,
}
